/**
 * The flight maths of a fortress skin's companions (views/fortressCompanion.ts; owner 2026-10-20: "the dragon needs
 * work, as does its flame breath, and the flapping of the wings in both it and the owls"). Pure numbers, no Three.js,
 * so it is unit-tested (test/companionFlight.test.ts):
 *
 *   swoopAt        the act's dive envelope, 0 → 1 → 0 with no kink at either end (the old sin(πk) jerked the flyer's
 *                  climb rate at the start and end of every swoop)
 *   bankFor        a coordinated turn's bank, atan(v·ω / g), clamped: a faster or tighter turn leans further
 *   pitchFor       nose up on a climb, down on a dive, from the flight path's own slope
 *   ease           frame-rate independent exponential follow (the old `min(1, dt·k)` changed feel with the frame rate)
 *   wingRate       the fly clip's time scale for a wingbeat of `cruiseHz`, quicker on a climb, easier on a descent
 *   Gait           flap or glide, with hysteresis and a minimum hold so the two clips cross-fade, never flicker
 *   breathTiming   when in the act the breath clip starts, so its jaw-open window is centred on the dive's low point
 *   breathLevel    the stream's strength 0..1 from the breath clip's time (jaw open window, eased in and out)
 *   launchSpeed    the speed a flame puff needs to carry `reach` under the VFX particles' linear drag in `t` seconds
 *   coneRadius     the stream's half-width at a fraction of its length: narrow at the jaw, wide where it lands
 *   lateralPhase   where on its loop a flyer crosses the camera's view side-on (its act is timed there, so the breath
 *                  reads as a long diagonal across the screen, not a blast at or away from the camera)
 */

/** A flyer's height kept `clear` over the fortress's top `top` (no `clear`: unchanged). */
export function clearOf(y: number, top: number, clear: number | undefined): number {
  return clear === undefined ? y : Math.max(y, top + clear);
}

/** The act's dive, 0 → 1 → 0 over k in 0..1 (sin²: zero slope at both ends, so the climb rate never jumps). */
export function swoopAt(k: number): number {
  if (!(k > 0) || k >= 1) return 0;
  const s = Math.sin(Math.PI * k);
  return s * s;
}

/** The bank (rad) of a coordinated turn at horizontal speed `v` and yaw rate `w` (rad/s), under gravity `g`, clamped. */
export function bankFor(v: number, w: number, g: number, max: number): number {
  const b = Math.atan((v * w) / g);
  return Math.max(-max, Math.min(max, b));
}

/** The pitch (rad, nose down positive like the old code's `-atan`) of a flight path climbing `vy` at horizontal speed `vh`. */
export function pitchFor(vy: number, vh: number, gain: number, max: number): number {
  const p = -Math.atan2(vy, Math.max(1e-3, vh)) * gain;
  return Math.max(-max, Math.min(max, p));
}

/** Moves `cur` toward `target` at `rate` (1/s), independent of the frame rate. */
export function ease(cur: number, target: number, rate: number, dt: number): number {
  return cur + (target - cur) * (1 - Math.exp(-rate * dt));
}

/** The wingbeat's tunables (CompanionSpec.gait). */
export interface GaitSpec {
  /** Wingbeats a second in level flight. */
  readonly cruiseHz: number;
  /** Wingbeats in one cycle of the `fly` clip (the clip may hold two). */
  readonly beats: number;
  /** How much a climb (units/s) quickens the beat (×(1 + gain·climb)), and how much a descent eases it. */
  readonly climbGain: number;
  /** Climb rate (units/s) below which the flyer glides, and above which it flaps again (hysteresis). */
  readonly glideBelow: number;
  readonly flapAbove: number;
  /** Shortest time (s) in either gait before it may change, and the clips' cross-fade (s). */
  readonly hold: number;
  readonly fade: number;
}

/** The beat's limits as multiples of the cruise rate: a dive never stops the wings dead, a climb never blurs them. */
export const WING_RATE = { min: 0.7, max: 1.45 } as const;

/** The `fly` clip's time scale for a wingbeat of `g.cruiseHz` at climb rate `climb` (units/s) on a clip `clipSeconds` long. */
export function wingRate(g: GaitSpec, clipSeconds: number, climb: number): number {
  const base = (g.cruiseHz * clipSeconds) / g.beats;
  const k = Math.max(WING_RATE.min, Math.min(WING_RATE.max, 1 + g.climbGain * climb));
  return base * k;
}

/** Flap or glide: a descent past `glideBelow` glides, a climb past `flapAbove` flaps, each held at least `hold` s. */
export class Gait {
  gliding = false;
  private held = 0;

  constructor(private readonly g: GaitSpec) {}

  /** Advances by `dt` at climb rate `climb`; returns true when the gait changed this step. */
  step(dt: number, climb: number): boolean {
    this.held += dt;
    if (this.held < this.g.hold) return false;
    const want = this.gliding ? climb < this.g.flapAbove : climb < this.g.glideBelow;
    if (want === this.gliding) return false;
    this.gliding = want;
    this.held = 0;
    return true;
  }
}

/** The breath's tunables (CompanionSpec.breath): the clip's jaw-open window and how the stream is thrown. */
export interface BreathSpec {
  /** Seconds into the `breath` clip (at time scale 1) that the jaw opens and closes. */
  readonly open: readonly [number, number];
  /** The clip's time scale (below 1 holds the jaw open longer: a longer breath). */
  readonly rate: number;
  /** Seconds the stream takes to swell at the jaw's opening and to die at its close (clip time). */
  readonly ramp: number;
  /**
   * The mouth, in the model's own units: the lower lip's tip in the `jaw` node's frame and the upper lip's (the snout)
   * in the `head` node's frame (the nodes are hinges, not lips; the model faces −z). The fire leaves from between them,
   * so it follows the jaw as it drops open and the head as it pitches.
   */
  readonly lips: { readonly jaw: readonly [number, number, number]; readonly head: readonly [number, number, number] };
  /** How much the stream follows the snout's own direction rather than the line to where it is aimed (0..1). */
  readonly snout: number;
  /** Where the stream lands: this far ahead of the flyer along its heading (world units), on the ground. */
  readonly aim: number;
  /** The longest it reaches (world units); a stream aimed further stops short in the air. */
  readonly reach: number;
  /** Its half-width at the jaw and where it lands (world units). */
  readonly width: readonly [number, number];
}

/** When the act's clip starts (s into the act) so the jaw-open window is centred on the dive's low point (`seconds`/2). */
export function breathTiming(b: BreathSpec, actSeconds: number): number {
  const mid = (b.open[0] + b.open[1]) / 2 / b.rate;
  return Math.max(0, actSeconds / 2 - mid);
}

/** The stream's strength 0..1 at `clipTime` s into the breath clip (clip seconds, i.e. already × the time scale). */
export function breathLevel(b: BreathSpec, clipTime: number): number {
  const [a, z] = b.open;
  if (!(clipTime > a) || clipTime >= z) return 0;
  const r = Math.max(1e-3, b.ramp);
  return Math.min(1, (clipTime - a) / r, (z - clipTime) / r);
}

/**
 * The launch speed for a puff to travel `reach` in `t` seconds when the VFX slows it by `drag` (its update multiplies
 * the velocity by 1 − drag·dt each frame: an exponential decay, distance = v₀/drag · (1 − e^(−drag·t))).
 */
export function launchSpeed(reach: number, drag: number, t: number): number {
  if (drag <= 1e-6) return reach / t;
  return (reach * drag) / (1 - Math.exp(-drag * t));
}

/** The stream's half-width `u` (0 at the jaw … 1 where it lands) along its length: linear from `width[0]` to `width[1]`. */
export function coneRadius(b: BreathSpec, u: number): number {
  const k = Math.max(0, Math.min(1, u));
  return b.width[0] + (b.width[1] - b.width[0]) * k;
}

/**
 * The loop phase (0..1, the loop's u: 0.25 is the gate) nearest `near` where a flyer on an ellipse squashed by `squash`
 * flies side-on to a camera looking along world (`fx`, `fz`), the loop turned by `yaw` (as Object3D.applyAxisAngle about +y).
 */
export function lateralPhase(fx: number, fz: number, yaw: number, squash: number, near: number): number {
  // the camera's heading in the loop's own frame (the loop's rotation undone)
  const c = Math.cos(yaw), s = Math.sin(yaw);
  const lx = fx * c - fz * s, lz = fx * s + fz * c;
  if (Math.hypot(lx, lz) < 1e-6) return near;
  // the tangent (−sin a, squash·cos a) is square to the heading where tan a = squash·lz / lx; two such points, half a lap apart
  const a = Math.atan2(squash * lz, lx);
  const u0 = (((a / (Math.PI * 2)) % 1) + 1) % 1, u1 = (u0 + 0.5) % 1;
  const dist = (u: number): number => { const d = Math.abs(u - near) % 1; return Math.min(d, 1 - d); };
  return dist(u0) <= dist(u1) ? u0 : u1;
}
